import React from "react";
import { seriesColor, CHART_INK, chartMax } from "./charts";

const PAD = 12;
const TITLE_H = 20;
const LEGEND_H = 16;
const GAP = 2;
const LABEL = 9;

const fmt = (n) => {
  const v = Number(n) || 0;
  return Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(1)}k` : String(Math.round(v * 10) / 10);
};

function Frame({ el, children, legend }) {
  return (
    <g>
      <rect x={el.x} y={el.y} width={el.w} height={el.h} rx={8}
        fill="rgba(255,255,255,0.72)" stroke="rgba(15,23,42,0.10)" strokeWidth={1} />
      {el.title && (
        <text x={el.x + PAD} y={el.y + PAD + 8} fontSize={11} fontWeight={700}
          fill={CHART_INK.title} fontFamily="'DM Sans', sans-serif">
          {el.title}
        </text>
      )}
      {children}
      {legend}
    </g>
  );
}

function Legend({ el, series }) {
  if (!series || series.length < 2) return null;
  const y = el.y + el.h - PAD + 2;
  let cursor = el.x + PAD;
  return (
    <g>
      {series.map((s, i) => {
        const label = s.name || `Series ${i + 1}`;
        const node = (
          <g key={label + i} transform={`translate(${cursor} ${y})`}>
            <rect x={0} y={-7} width={8} height={8} rx={2} fill={seriesColor(i)} />
            <text x={12} y={0} fontSize={9} fill={CHART_INK.label} fontFamily="'DM Sans', sans-serif">
              {label}
            </text>
          </g>
        );
        cursor += 12 + label.length * 5 + 12;
        return node;
      })}
    </g>
  );
}

const GUTTER = {
  bar: 26, stacked: 26, line: 26, area: 26,
  column: 72,
  pie: 0, donut: 0, table: 0, timeline: 0,
};

function plotRect(el, series) {
  const gutter = GUTTER[el.chartType] ?? 26;
  const hasCategoryRow = ["bar", "stacked", "line", "area"].includes(el.chartType);
  const top = el.y + PAD + (el.title ? TITLE_H : 0);
  const bottom = el.y + el.h - PAD
    - (series && series.length > 1 ? LEGEND_H : 0)
    - (hasCategoryRow ? 12 : 0);
  return {
    x: el.x + PAD + gutter,
    y: top,
    w: Math.max(el.w - PAD * 2 - gutter, 20),
    h: Math.max(bottom - top, 20),
  };
}

function Grid({ box, max, ticks = 3 }) {
  const lines = Array.from({ length: ticks + 1 }, (_, i) => i / ticks);
  return (
    <g>
      {lines.map((f) => {
        const y = box.y + box.h - f * box.h;
        return (
          <g key={f}>
            <line x1={box.x} y1={y} x2={box.x + box.w} y2={y}
              stroke={CHART_INK.grid} strokeWidth={1} />
            <text x={box.x - 6} y={y + 3} textAnchor="end" fontSize={8}
              fill={CHART_INK.muted} fontFamily="'DM Sans', sans-serif">
              {fmt(max * f)}
            </text>
          </g>
        );
      })}
    </g>
  );
}

function CategoryLabels({ box, categories, horizontal }) {
  const n = categories.length || 1;
  if (horizontal) {
    const band = box.h / n;
    return (
      <g>
        {categories.map((c, i) => (
          <text key={i} x={box.x - 6} y={box.y + band * i + band / 2 + 3} textAnchor="end"
            fontSize={8} fill={CHART_INK.label} fontFamily="'DM Sans', sans-serif">
            {c}
          </text>
        ))}
      </g>
    );
  }
  const band = box.w / n;
  return (
    <g>
      {categories.map((c, i) => (
        <text key={i} x={box.x + band * i + band / 2} y={box.y + box.h + 12} textAnchor="middle"
          fontSize={8} fill={CHART_INK.label} fontFamily="'DM Sans', sans-serif">
          {c}
        </text>
      ))}
    </g>
  );
}

function VerticalBars({ el, box, data, stacked }) {
  const categories = data.categories || [];
  const series = data.series || [];
  const max = chartMax(data, stacked);
  const band = box.w / (categories.length || 1);
  const groupW = band * 0.62;

  return (
    <g>
      <Grid box={box} max={max} />
      {categories.map((_, ci) => {
        const cx = box.x + band * ci + band / 2;
        if (stacked) {
          let acc = 0;
          return (
            <g key={ci}>
              {series.map((s, si) => {
                const v = Number(s.values?.[ci]) || 0;
                const h = (v / max) * box.h;
                const y = box.y + box.h - acc - h;
                acc += h + GAP;
                if (h <= 0) return null;
                return (
                  <rect key={si} x={cx - groupW / 2} y={y} width={groupW}
                    height={Math.max(h - GAP, 1)} rx={4} fill={seriesColor(si)} />
                );
              })}
              <text x={cx} y={box.y + box.h - acc - 4} textAnchor="middle" fontSize={LABEL}
                fontWeight={600} fill={CHART_INK.label} fontFamily="'DM Sans', sans-serif">
                {fmt(series.reduce((t, s) => t + (Number(s.values?.[ci]) || 0), 0))}
              </text>
            </g>
          );
        }
        const barW = (groupW - GAP * (series.length - 1)) / (series.length || 1);
        return (
          <g key={ci}>
            {series.map((s, si) => {
              const v = Number(s.values?.[ci]) || 0;
              const h = (v / max) * box.h;
              const x = cx - groupW / 2 + si * (barW + GAP);
              return (
                <g key={si}>
                  <rect x={x} y={box.y + box.h - h} width={Math.max(barW, 1)}
                    height={Math.max(h, 1)} rx={4} fill={seriesColor(si)} />
                  {series.length === 1 && (
                    <text x={x + barW / 2} y={box.y + box.h - h - 4} textAnchor="middle"
                      fontSize={LABEL} fontWeight={600} fill={CHART_INK.label}
                      fontFamily="'DM Sans', sans-serif">
                      {fmt(v)}
                    </text>
                  )}
                </g>
              );
            })}
          </g>
        );
      })}
      <line x1={box.x} y1={box.y + box.h} x2={box.x + box.w} y2={box.y + box.h}
        stroke={CHART_INK.axis} strokeWidth={1} />
      <CategoryLabels box={box} categories={categories} />
    </g>
  );
}

function HorizontalBars({ box, data }) {
  const categories = data.categories || [];
  const series = data.series || [];
  const max = chartMax(data, false);
  const band = box.h / (categories.length || 1);
  const barH = Math.min(band * 0.55, 22);

  return (
    <g>
      {categories.map((_, ci) => {
        const cy = box.y + band * ci + band / 2;
        const v = Number(series[0]?.values?.[ci]) || 0;
        const w = (v / max) * box.w;
        return (
          <g key={ci}>
            <rect x={box.x} y={cy - barH / 2} width={Math.max(w, 1)} height={barH}
              rx={4} fill={seriesColor(0)} />
            <text x={box.x + w + 6} y={cy + 3} fontSize={LABEL} fontWeight={600}
              fill={CHART_INK.label} fontFamily="'DM Sans', sans-serif">
              {fmt(v)}
            </text>
          </g>
        );
      })}
      <line x1={box.x} y1={box.y} x2={box.x} y2={box.y + box.h}
        stroke={CHART_INK.axis} strokeWidth={1} />
      <CategoryLabels box={box} categories={categories} horizontal />
    </g>
  );
}

function LineChart({ box, data, area }) {
  const categories = data.categories || [];
  const series = data.series || [];
  const max = chartMax(data, false);
  const n = categories.length || 1;
  const step = n > 1 ? box.w / (n - 1) : box.w;
  const pointAt = (v, i) => ({
    x: box.x + step * i,
    y: box.y + box.h - ((Number(v) || 0) / max) * box.h,
  });

  return (
    <g>
      <Grid box={box} max={max} />
      {series.map((s, si) => {
        const pts = (s.values || []).map(pointAt);
        if (!pts.length) return null;
        const d = pts.map((p, i) => `${i ? "L" : "M"} ${p.x} ${p.y}`).join(" ");
        return (
          <g key={si}>
            {area && (
              <path
                d={`${d} L ${pts[pts.length - 1].x} ${box.y + box.h} L ${pts[0].x} ${box.y + box.h} Z`}
                fill={seriesColor(si)} opacity={0.16} />
            )}
            <path d={d} fill="none" stroke={seriesColor(si)} strokeWidth={2}
              strokeLinecap="round" strokeLinejoin="round" />
            {pts.map((p, i) => (
              <circle key={i} cx={p.x} cy={p.y} r={4} fill={seriesColor(si)}
                stroke={CHART_INK.surface} strokeWidth={2} />
            ))}
            {[0, pts.length - 1].filter((i, k, a) => pts.length > 1 || k === 0).map((i) => (
              <text key={`l${i}`} x={pts[i].x} y={pts[i].y - 8} textAnchor={i === 0 ? "start" : "end"}
                fontSize={LABEL} fontWeight={600} fill={CHART_INK.label}
                fontFamily="'DM Sans', sans-serif">
                {fmt(s.values?.[i])}
              </text>
            ))}
          </g>
        );
      })}
      <line x1={box.x} y1={box.y + box.h} x2={box.x + box.w} y2={box.y + box.h}
        stroke={CHART_INK.axis} strokeWidth={1} />
      <CategoryLabels box={box} categories={categories} />
    </g>
  );
}

function PieChart({ el, box, data, donut }) {
  const categories = data.categories || [];
  const values = (data.series?.[0]?.values || []).map((v) => Number(v) || 0);
  const total = values.reduce((a, b) => a + b, 0) || 1;
  const r = Math.min(box.w, box.h) / 2 - 4;
  const cx = box.x + box.w / 2;
  const cy = box.y + box.h / 2;
  const inner = donut ? r * 0.58 : 0;

  let angle = -Math.PI / 2;
  return (
    <g>
      {values.map((v, i) => {
        const sweep = (v / total) * Math.PI * 2;
        const end = angle + sweep;
        const large = sweep > Math.PI ? 1 : 0;
        const p = (rad, a) => `${cx + rad * Math.cos(a)} ${cy + rad * Math.sin(a)}`;
        const d = donut
          ? `M ${p(r, angle)} A ${r} ${r} 0 ${large} 1 ${p(r, end)} L ${p(inner, end)} A ${inner} ${inner} 0 ${large} 0 ${p(inner, angle)} Z`
          : `M ${cx} ${cy} L ${p(r, angle)} A ${r} ${r} 0 ${large} 1 ${p(r, end)} Z`;
        const mid = angle + sweep / 2;
        const lx = cx + (r + 12) * Math.cos(mid);
        const ly = cy + (r + 12) * Math.sin(mid);
        angle = end;
        if (v <= 0) return null;
        return (
          <g key={i}>
            <path d={d} fill={seriesColor(i)} stroke={CHART_INK.surface} strokeWidth={2} />
            <text x={lx} y={ly} textAnchor={Math.cos(mid) < 0 ? "end" : "start"}
              fontSize={LABEL} fontWeight={600} fill={CHART_INK.label}
              fontFamily="'DM Sans', sans-serif">
              {`${categories[i] ?? ""} ${Math.round((v / total) * 100)}%`}
            </text>
          </g>
        );
      })}
      {donut && (
        <text x={cx} y={cy + 4} textAnchor="middle" fontSize={13} fontWeight={700}
          fill={CHART_INK.title} fontFamily="'DM Sans', sans-serif">
          {fmt(total)}
        </text>
      )}
    </g>
  );
}

function TableChart({ el, box, data }) {
  const columns = data.columns || [];
  const rows = data.rows || [];
  const colW = box.w / (columns.length || 1);
  const rowH = Math.min(24, box.h / (rows.length + 1 || 1));

  return (
    <g>
      {columns.map((c, i) => (
        <text key={`h${i}`} x={box.x + colW * i + 6} y={box.y + 12} fontSize={9}
          fontWeight={700} fill={CHART_INK.title} fontFamily="'DM Sans', sans-serif">
          {c}
        </text>
      ))}
      <line x1={box.x} y1={box.y + 18} x2={box.x + box.w} y2={box.y + 18}
        stroke={CHART_INK.axis} strokeWidth={1} />
      {rows.map((row, ri) => (
        <g key={ri}>
          {row.map((cell, ci) => (
            <text key={ci} x={box.x + colW * ci + 6} y={box.y + 18 + rowH * (ri + 1) - 6}
              fontSize={9} fill={CHART_INK.label} fontFamily="'DM Sans', sans-serif">
              {String(cell)}
            </text>
          ))}
          <line x1={box.x} y1={box.y + 18 + rowH * (ri + 1)} x2={box.x + box.w}
            y2={box.y + 18 + rowH * (ri + 1)} stroke={CHART_INK.grid} strokeWidth={1} />
        </g>
      ))}
    </g>
  );
}

function TimelineChart({ box, data }) {
  const events = data.events || [];
  const n = events.length || 1;
  const step = n > 1 ? box.w / (n - 1) : box.w;
  const y = box.y + box.h / 2;

  return (
    <g>
      <line x1={box.x} y1={y} x2={box.x + box.w} y2={y}
        stroke={CHART_INK.axis} strokeWidth={2} strokeLinecap="round" />
      {events.map((ev, i) => {
        const x = box.x + step * i;
        return (
          <g key={i}>
            <circle cx={x} cy={y} r={5} fill={seriesColor(i)}
              stroke={CHART_INK.surface} strokeWidth={2} />
            <text x={x} y={y - 12} textAnchor="middle" fontSize={9} fontWeight={600}
              fill={CHART_INK.title} fontFamily="'DM Sans', sans-serif">
              {ev.label}
            </text>
            <text x={x} y={y + 18} textAnchor="middle" fontSize={8}
              fill={CHART_INK.muted} fontFamily="'DM Sans', sans-serif">
              {ev.when}
            </text>
          </g>
        );
      })}
    </g>
  );
}

export default function ChartEl({ el }) {
  const data = el.data || {};
  const series = data.series || [];
  const box = plotRect(el, series);
  const legend = <Legend el={el} series={series} />;

  let body = null;
  switch (el.chartType) {
    case "bar":      body = <VerticalBars el={el} box={box} data={data} />; break;
    case "stacked":  body = <VerticalBars el={el} box={box} data={data} stacked />; break;
    case "column":   body = <HorizontalBars box={box} data={data} />; break;
    case "line":     body = <LineChart box={box} data={data} />; break;
    case "area":     body = <LineChart box={box} data={data} area />; break;
    case "pie":      body = <PieChart el={el} box={box} data={data} />; break;
    case "donut":    body = <PieChart el={el} box={box} data={data} donut />; break;
    case "table":    body = <TableChart el={el} box={box} data={data} />; break;
    case "timeline": body = <TimelineChart box={box} data={data} />; break;
    default:         body = null;
  }

  return <Frame el={el} legend={legend}>{body}</Frame>;
}
